// src/components/pages/admin/organisms/EpisodeForm.js
import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Card,
  CardContent,
  CardMedia,
} from "@mui/material";
import { db } from "../../../../firebase";
import {
  collection,
  addDoc,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useNavigate } from "react-router-dom";
import { US_RATINGS, TV_CATEGORIES } from "../../../../constants/constants";

// Utility to convert Firestore timestamp to a JS Date
const convertTimestamp = (timestamp) => {
  return timestamp && timestamp.toDate
    ? timestamp.toDate()
    : new Date(timestamp);
};

const EpisodeForm = ({ initialData = null, onSubmit, isEdit = false }) => {
  const [title, setTitle] = useState(initialData ? initialData.title : "");
  const [description, setDescription] = useState(
    initialData ? initialData.description : ""
  );
  // Hidden field; will be auto-populated later from video metadata
  const [length, setLength] = useState(initialData ? initialData.length : "");
  const [contentRating, setContentRating] = useState(
    initialData ? initialData.contentRating : ""
  );
  const [category, setCategory] = useState(
    initialData ? initialData.category : ""
  );
  const [season, setSeason] = useState(initialData ? initialData.season : "1");
  const [thumbnail, setThumbnail] = useState(
    initialData ? initialData.thumbnail : ""
  );
  const [videoUrl, setVideoUrl] = useState(
    initialData ? initialData.videoUrl : ""
  );
  const [goLiveDate, setGoLiveDate] = useState(
    initialData && initialData.goLiveDate
      ? convertTimestamp(initialData.goLiveDate)
      : null
  );
  const [goDarkDate, setGoDarkDate] = useState(
    initialData && initialData.goDarkDate
      ? convertTimestamp(initialData.goDarkDate)
      : null
  );
  const [seriesId, setSeriesId] = useState(
    initialData ? initialData.seriesId : ""
  );
  const [seriesOptions, setSeriesOptions] = useState([]);

  const navigate = useNavigate();

  // Fetch available series from Firestore, including default category and rating
  useEffect(() => {
    const fetchSeries = async () => {
      try {
        const seriesSnapshot = await getDocs(collection(db, "series"));
        const seriesData = seriesSnapshot.docs.map((doc) => ({
          id: doc.id,
          title: doc.data().seriesTitle || "Untitled Series",
          category: doc.data().category || "",
          contentRating: doc.data().contentRating || "",
        }));
        setSeriesOptions(seriesData);
      } catch (error) {
        console.error("Error fetching series:", error);
      }
    };

    fetchSeries();
  }, []);

  // When a series is selected, prefill category and rating from the series defaults
  useEffect(() => {
    if (seriesId) {
      const selectedSeries = seriesOptions.find((s) => s.id === seriesId);
      if (selectedSeries) {
        setCategory(selectedSeries.category);
        setContentRating(selectedSeries.contentRating);
      }
    }
  }, [seriesId, seriesOptions]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const formData = {
      title,
      description,
      length,
      contentRating,
      category,
      season,
      thumbnail,
      videoUrl,
      goLiveDate: goLiveDate ? goLiveDate : null,
      goDarkDate: goDarkDate ? goDarkDate : null,
      seriesId: seriesId || null,
      likeCount: initialData ? initialData.likeCount : 0,
      ...(isEdit ? {} : { createdAt: serverTimestamp() }),
    };
    try {
      if (onSubmit) {
        await onSubmit(formData);
      } else {
        await addDoc(collection(db, "episodes"), {
          ...formData,
          createdAt: serverTimestamp(),
        });
        alert("Episode created successfully");
        // Reset fields after creation
        setTitle("");
        setDescription("");
        setLength("");
        setContentRating("");
        setCategory("");
        setSeason("1");
        setThumbnail("");
        setVideoUrl("");
        setGoLiveDate(null);
        setGoDarkDate(null);
        setSeriesId("");
      }
    } catch (error) {
      console.error("Error submitting episode:", error);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box component="form" onSubmit={handleFormSubmit} sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          {isEdit ? "Edit Episode" : "Create New Episode"}
        </Typography>
        <Grid container spacing={2}>
          {/* Preview Panel */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Preview
            </Typography>
            <Card sx={{ display: "flex", flexDirection: "column" }}>
              {videoUrl ? (
                <CardMedia
                  component="video"
                  controls
                  src={videoUrl}
                  poster={thumbnail || undefined}
                  // Use the CSS aspectRatio property to enforce 16:9
                  sx={{
                    width: "100%",
                    aspectRatio: "16/9",
                    objectFit: "cover",
                  }}
                  onLoadedMetadata={(e) => {
                    const duration = e.target.duration;
                    setLength(Math.floor(duration)); // update the length state in seconds
                  }}
                />
              ) : (
                thumbnail && (
                  <CardMedia
                    component="img"
                    image={thumbnail}
                    alt="Video Poster"
                    sx={{
                      width: "100%",
                      aspectRatio: "16/9",
                      objectFit: "cover",
                    }}
                  />
                )
              )}
              <CardContent>
                <Typography variant="h6">{title || "Video Title"}</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {description || "Video description..."}
                </Typography>
                <Typography variant="body2">
                  Category: {category || "N/A"}
                </Typography>
                <Typography variant="body2">
                  Rating: {contentRating || "N/A"}
                </Typography>
                {length && (
                  <Typography variant="body2">
                    Duration: {length} seconds
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Title */}
          <Grid item xs={12}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
            />
          </Grid>
          {/* Description */}
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={4}
            />
          </Grid>

          {/* Series Dropdown at the top */}
          <Grid item xs={12} sm={6}>
            {seriesOptions.length > 0 ? (
              <FormControl fullWidth>
                <InputLabel id="series-select-label">Series</InputLabel>
                <Select
                  labelId="series-select-label"
                  label="Series"
                  value={seriesId}
                  onChange={(e) => setSeriesId(e.target.value)}
                >
                  {seriesOptions.map((series) => (
                    <MenuItem key={series.id} value={series.id}>
                      {series.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Button
                variant="outlined"
                onClick={() => navigate("/admin/cms/series/new")}
              >
                Create a Series
              </Button>
            )}
          </Grid>
          {/* Season Number */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Season"
              type="number"
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              fullWidth
              required
              inputProps={{ min: 1 }}
            />
          </Grid>
          {/* Content Rating */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel id="content-rating-label">Content Rating</InputLabel>
              <Select
                labelId="content-rating-label"
                label="Content Rating"
                value={contentRating}
                onChange={(e) => setContentRating(e.target.value)}
              >
                {US_RATINGS.map((rating) => (
                  <MenuItem key={rating} value={rating}>
                    {rating}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Category */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {TV_CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Thumbnail URL */}
          <Grid item xs={12}>
            <TextField
              label="Thumbnail URL"
              value={thumbnail}
              onChange={(e) => setThumbnail(e.target.value)}
              fullWidth
            />
          </Grid>
          {/* Video URL */}
          <Grid item xs={12}>
            <TextField
              label="Video URL"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              fullWidth
              required
            />
          </Grid>
          {/* Go Live Date */}
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Go Live Date"
              value={goLiveDate}
              onChange={(newValue) => setGoLiveDate(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          {/* Go Dark Date */}
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Go Dark Date"
              value={goDarkDate}
              onChange={(newValue) => setGoDarkDate(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          <Grid item xs={12}>
            <Button type="submit" variant="contained" color="primary" fullWidth>
              {isEdit ? "Update Episode" : "Create Episode"}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default EpisodeForm;
